import { useEffect, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import useAuth from '@sdk/authentication/useAuth';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text12 from '@sdk/components/ui/typography/Text12';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { Feature } from '@sdk/common/features';
import ToggleGroup, {
  ToggleGroupOption,
  getToggleGroupOutcomePropertyValues,
} from '@sdk/components/forms/components/toggleGroup/ToggleGroup';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import SmallHelpBlock from '../SmallHelpBlock';
import PremiumGuard from '../../../premiumGuard/PremiumGuard';
import usePremiumGuard from '../../../premiumGuard/usePremiumGuard';
import { PremiumModalNames } from '../../../premiumGuard/constants/modalNames';
import { useUserSettings } from '../../UserSettingsContext';

import { UseShiftAlarmsType } from './shiftAlarms/useShiftAlarms';
import ShiftAlarms from './shiftAlarms/ShiftAlarms';

const StyledColumn = styled(Column)`
  align-items: flex-end;
`;

const ShiftAlarmsNotifications: React.FC<React.PropsWithChildren<UseShiftAlarmsType>> = ({
  alarmsModel,
  addAnotherAlarm,
  setAlarmsModel,
  canAddAnotherAlarm,
  removeAlarm,
  onChangeAlarmUnit,
  onChangeAlarmValue,
}) => {
  const { userSettings, onUserSettingsChange } = useUserSettings();
  const {
    shiftAlarmNotifyViaSms,
    shiftAlarmNotifyViaPush,
    shiftAlarmNotifyViaEmail,
    shiftAlarmEnabled,
  } = userSettings;
  const initializeToggleGroupOptions = (
    shiftAlarmNotifyViaSms: boolean,
    shiftAlarmNotifyViaEmail: boolean,
    shiftAlarmNotifyViaPush: boolean
  ): ToggleGroupOption[] => {
    return [
      {
        name: 'shiftAlarmNotifyViaSms',
        label: 'SMS',
        value: shiftAlarmNotifyViaSms,
        disabled: !currentUser.org.isPremium,
        renderWrapper: function PremiumGuardComponent(children: JSX.Element): JSX.Element {
          return (
            <PremiumGuard
              key="premium-shiftAlarmNotifyViaSms"
              hasAccess={currentUser.org.isPremium}
              tooltipContent={getPremiumGuardTooltipContent()}
              premiumGuardModalName={PremiumModalNames.smsAlarms}>
              {children}
            </PremiumGuard>
          );
        },
      },
      {
        name: 'shiftAlarmNotifyViaEmail',
        label: 'Email',
        value: shiftAlarmNotifyViaEmail,
        disabled: false,
      },
      {
        name: 'shiftAlarmNotifyViaPush',
        label: 'Mobile',
        value: shiftAlarmNotifyViaPush,
        disabled: !currentUser.canReceivePush,
      },
    ];
  };

  const { isOrgFeatureEnabled, currentUser } = useAuth();
  const { getPremiumGuardTooltipContent } = usePremiumGuard();
  const [shiftAlarmsToggleGroupOptions, setShiftAlarmsToggleGroupOptions] = useState(() =>
    initializeToggleGroupOptions(
      shiftAlarmNotifyViaSms,
      shiftAlarmNotifyViaEmail,
      shiftAlarmNotifyViaPush
    )
  );

  useEffect(() => {
    setShiftAlarmsToggleGroupOptions(
      initializeToggleGroupOptions(
        shiftAlarmNotifyViaSms,
        shiftAlarmNotifyViaEmail,
        shiftAlarmNotifyViaPush
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftAlarmNotifyViaSms, shiftAlarmNotifyViaEmail, shiftAlarmNotifyViaPush]);

  const onShiftAlarmsNotificationChange = (value: ToggleGroupOption[]): void => {
    let shiftAlarmEnabledCopy = shiftAlarmEnabled;

    if (!value.filter((alarm) => alarm.value).length) {
      shiftAlarmEnabledCopy = false;
    }

    setShiftAlarmsToggleGroupOptions(value);
    onUserSettingsChange({
      ...getToggleGroupOutcomePropertyValues(value),
      shiftAlarmEnabled: shiftAlarmEnabledCopy,
    });
  };

  return (
    <SettingsSection>
      <CollapsibleSettingsSection
        withSeparator
        isActive={isOrgFeatureEnabled(Feature.alarms) && shiftAlarmEnabled}
        headerItem={
          <SettingsSwitcherRow
            subtitle={gettext('Enable shift alarms, so you get a reminder for upcoming shifts.')}
            value={shiftAlarmEnabled}
            onChange={(value): void => onUserSettingsChange({ shiftAlarmEnabled: value })}
            withMainHeader
          />
        }
        contentItem={
          <>
            <Column pBottom={20}>
              <RowSpaceBetween>
                <Column>
                  <Text14 semiBold themeColor={ThemeColorType.gray}>
                    {gettext('Time before shift')}
                  </Text14>
                  <Text14 themeColor={ThemeColorType.gray}>
                    {gettext('Send dashboard notifications:')}
                  </Text14>
                </Column>
                <Row>
                  <ShiftAlarms
                    alarmsModel={alarmsModel}
                    addAnotherAlarm={addAnotherAlarm}
                    setAlarmsModel={setAlarmsModel}
                    canAddAnotherAlarm={canAddAnotherAlarm}
                    removeAlarm={removeAlarm}
                    onChangeAlarmUnit={onChangeAlarmUnit}
                    onChangeAlarmValue={onChangeAlarmValue}
                    shiftAlarmEnabled={shiftAlarmEnabled}
                    onChange={(value: number[]): void =>
                      onUserSettingsChange({ shiftAlarms: value })
                    }
                  />
                </Row>
              </RowSpaceBetween>
            </Column>

            <Column pBottom={20}>
              <RowSpaceBetween>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Send notification via:')}
                </Text14>
                <StyledColumn>
                  <ToggleGroup
                    onChange={onShiftAlarmsNotificationChange}
                    options={shiftAlarmsToggleGroupOptions}
                  />
                  {!currentUser.canReceivePush && <SmallHelpBlock />}
                </StyledColumn>
              </RowSpaceBetween>
              <Row pTop={10}>
                <Text12 themeColor={ThemeColorType.gray} textAlign="justify">
                  {gettext('By providing a mobile number, you give Toast, Sling, and')}
                  {currentUser.org.name}
                  {gettext(
                    "permission to contact you using automated text messages to provide transactional messages such as shift alarms. Message and data rates may apply, frequency varies. Reply STOP to opt out. Service is subject to Toast's Terms of Service and Privacy Statement and Merchant's Terms and Policies. Info for CA residents available "
                  )}
                  <a href="https://central.toasttab.com/s/article/How-Toast-can-support-your-California-Consumer-Privacy-Act-compliance-efforts">
                    {gettext('here')}
                  </a>
                  .
                </Text12>
              </Row>
            </Column>
          </>
        }
      />
    </SettingsSection>
  );
};

export default ShiftAlarmsNotifications;
